import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  ListItemText,
  Typography,
  Box,
} from "@mui/material";
import React from "react";
import { useLocales } from "../../hooks/useLocales";
import { Icon } from "@iconify/react";
import { languages } from "../../enums/languages.enum";
import { t } from "i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

type LanguageSelectorProps = {
  langs: {
    key: string;
    value: string;
    flag: string;
  }[];
  onSelect: (lang: string) => void;
  currentLang: string;
  loading?: boolean;
};

function LanguageSelector({
  currentLang,
  langs,
  onSelect,
  loading = false,
}: LanguageSelectorProps) {
  const userLang = useSelector<RootState>((state) => state.user.user?.lang) as
    | undefined
    | string;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      {/* Wrapper Box to align the CircularProgress perfectly over the IconButton border */}
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <IconButton
          id="basic-button"
          aria-controls={open ? "basic-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          disabled={loading}
          sx={{ p: 0.5, cursor: loading ? "progress" : "pointer" }}
        >
          <Icon
            width={32}
            height={32}
            style={{ borderRadius: "50%" }}
            icon={
              langs.find((lang) => lang.key === currentLang)?.flag ??
              "circle-flags:lang-en"
            }
          />
        </IconButton>

        {/* Overlay spinner on top of the button border when loading is true */}
        {loading && (
          <CircularProgress
            size={40}
            sx={{
              color: "white",
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 1,
              pointerEvents: "none",
            }}
          />
        )}
      </Box>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuList>
          {languages.map((l) => (
            <MenuItem
              selected={l.key === currentLang}
              sx={{ gap: 1 }}
              key={l.key}
              onClick={() => {
                onSelect(l.key);
                handleClose();
              }}
            >
              <Icon
                fontSize={20}
                style={{ borderRadius: "50%" }}
                icon={l.flag}
              />
              <ListItemText
                secondary={
                  l.key === userLang && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {t("billing:default")}
                    </Typography>
                  )
                }
              >
                {t(l.value, { lng: l.key })}
              </ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </div>
  );
}

export default LanguageSelector;
